import React, { useEffect, useMemo, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

export default function MatkaDoublePana({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [sessionStatus, setSessionStatus] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0]
  );
  const [points, setPoints] = useState("");
  const [doublePana, setDoublePana] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: sessionStatus,
    bids,
    onPlaced: () => setBids([]),
  });

  const pointsValue = Number(points);
  const canAddBid =
    points.trim() !== "" && Number.isFinite(pointsValue) && pointsValue > 0;

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  useEffect(() => {
    const digits = doublePana.trim();
    if (!digits) {
      setSuggestions([]);
      setSuggestionsError("");
      setSuggestionsLoading(false);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(() => {
      setSuggestionsLoading(true);
      fetchMatkaSuggestions("double_pana", digits)
        .then((data) => {
          if (!active) return;
          const panas = Array.isArray(data?.panas) ? data.panas.map(String) : [];
          setSuggestions(panas);
          setSuggestionsError("");
        })
        .catch(() => {
          if (!active) return;
          setSuggestions([]);
          setSuggestionsError("Unable to load suggestions.");
        })
        .finally(() => {
          if (active) setSuggestionsLoading(false);
        });
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [doublePana]);

  const handleSelectSuggestion = (pana) => {
    if (!canAddBid || !suggestions.includes(pana)) return;

    setBids((currentBids) => {
      const existingBid = currentBids.find(
        (bid) => bid.pana === pana && bid.gameType === sessionStatus
      );
      if (existingBid) {
        return currentBids.map((bid) =>
          bid.id === existingBid.id
            ? { ...bid, points: Number(bid.points) + pointsValue }
            : bid
        );
      }

      return [
        {
          id: Date.now(),
          pana,
          points: pointsValue,
          gameType: sessionStatus,
        },
        ...currentBids,
      ];
    });
  };

  const handleSessionChange = (nextSession) => {
    setSessionStatus(nextSession);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextSession, (bid) => bid.pana)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Title */}
          <h1 className="matka-jodi-title mb-4">DOUBLE PANA</h1>

          {/* Game Type Dropdown */}
          <div className="matka-game-type-selector-wrapper mb-4">
            <label className="matka-game-type-label">GAME TYPE</label>
            <MatkaSessionSelect
            betDirection={selectedGameType?.betDirection}
            value={sessionStatus}
            onChange={handleSessionChange}
          />
          </div>

          <div className="matka-jodi-form mb-4">
            <div className="matka-jodi-input-group mb-4">
              <label className="matka-jodi-label">ENTER POINTS</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-jodi-input"
                placeholder="Enter Points (e.g. 100)"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
            </div>

            <div className="matka-jodi-input-group mb-4">
              <label className="matka-jodi-label">DOUBLE PANA</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-jodi-input"
                placeholder="Enter digits"
                value={doublePana}
                onChange={(e) => setDoublePana(e.target.value.replace(/\D/g, ""))}
              />
              {suggestionsLoading && (
                <p className="matka-pana-suggestion-status">Loading suggestions...</p>
              )}
              {suggestionsError && (
                <p className="matka-pana-suggestion-status">{suggestionsError}</p>
              )}
              {!suggestionsLoading && suggestions.length > 0 && (
                <div className="matka-pana-suggestions">
                  {suggestions.map((pana) => (
                    <button
                      key={pana}
                      type="button"
                      className="matka-pana-suggestion-btn"
                      disabled={!canAddBid}
                      onClick={() => handleSelectSuggestion(pana)}
                    >
                      {pana}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="matka-jodi-divider mb-4" />

          <div className="matka-jodi-table-container mb-4">
            <table className="matka-jodi-table">
              <thead>
                <tr>
                  <th className="th-jodi">PANA</th>
                  <th className="th-points text-center">POINTS</th>
                  <th className="th-gametype text-end">GAME TYPE</th>
                  <th className="th-action" aria-label="Delete"></th>
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                  <tr key={bid.id}>
                    <td className="td-jodi">{bid.pana}</td>
                    <td className="td-points text-center">{bid.points}</td>
                    <td className="td-gametype text-end">{bid.gameType}</td>
                    <td className="td-action text-end">
                      <button
                        type="button"
                        className="matka-jodi-delete-btn"
                        onClick={() => handleDeleteBid(bid.id)}
                        aria-label={`Delete pana ${bid.pana}`}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
